import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Activity, Clock, AlertTriangle, CheckCircle, Server, Database, Wifi, Users } from 'lucide-react';
import api from '../lib/axios';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  operational: { label: 'Operacional', color: 'bg-emerald-500', textColor: 'text-emerald-600', bgLight: 'bg-emerald-50' },
  degraded: { label: 'Degradado', color: 'bg-amber-500', textColor: 'text-amber-600', bgLight: 'bg-amber-50' },
  major_outage: { label: 'Indisponível', color: 'bg-red-500', textColor: 'text-red-600', bgLight: 'bg-red-50' },
};

export default function StatusPage() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statusRes, historyRes, incidentsRes] = await Promise.all([
          api.get('/monitoring/status').then(r => r.data),
          api.get('/monitoring/sla/history').then(r => r.data),
          api.get('/monitoring/incidents?status=open').then(r => r.data),
        ]);
        setStatus(statusRes);
        setHistory(historyRes.days || []);
        setIncidents(incidentsRes || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="status-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const cfg = statusConfig[status?.status] || statusConfig.operational;

  return (
    <div className="space-y-6" data-testid="status-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Status do Sistema</h1>
          <p className="text-sm text-slate-500 mt-1">Monitoramento em tempo real do Nautilus OS</p>
        </div>
        <Badge className={`${cfg.color} text-white text-sm px-3 py-1`} data-testid="overall-status-badge">
          {cfg.label}
        </Badge>
      </div>

      {/* Status Banner */}
      <Card className={`${cfg.bgLight} border-none`} data-testid="status-banner">
        <CardContent className="py-6">
          <div className="flex items-center gap-3">
            {status?.status === 'operational' ? (
              <CheckCircle className={`h-8 w-8 ${cfg.textColor}`} />
            ) : (
              <AlertTriangle className={`h-8 w-8 ${cfg.textColor}`} />
            )}
            <div>
              <p className={`text-lg font-semibold ${cfg.textColor}`}>
                {status?.status === 'operational' ? 'Todos os sistemas operacionais' : 'Alguns sistemas estão com problemas'}
              </p>
              <p className="text-sm text-slate-500">
                Última verificação: {status?.last_check?.timestamp ? new Date(status.last_check.timestamp).toLocaleString('pt-BR') : 'N/A'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="metric-availability">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <Activity className="h-3.5 w-3.5" />
              Disponibilidade (24h)
            </div>
            <p className="text-2xl font-bold text-slate-800">{status?.availability_24h?.toFixed(2)}%</p>
            <p className="text-xs text-slate-400">Meta: 99,50%</p>
          </CardContent>
        </Card>
        <Card data-testid="metric-response-time">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <Clock className="h-3.5 w-3.5" />
              Tempo de Resposta
            </div>
            <p className="text-2xl font-bold text-slate-800">{status?.avg_response_ms?.toFixed(0) || 0}ms</p>
            <p className="text-xs text-slate-400">p95: {status?.p95_response_ms?.toFixed(0) || 0}ms</p>
          </CardContent>
        </Card>
        <Card data-testid="metric-requests">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <Server className="h-3.5 w-3.5" />
              Requisições Hoje
            </div>
            <p className="text-2xl font-bold text-slate-800">{status?.total_requests_today || 0}</p>
            <p className="text-xs text-slate-400">Erros: {status?.error_rate_percent?.toFixed(3) || 0}%</p>
          </CardContent>
        </Card>
        <Card data-testid="metric-users">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
              <Users className="h-3.5 w-3.5" />
              Usuários Ativos
            </div>
            <p className="text-2xl font-bold text-slate-800">{status?.active_users || 0}</p>
            <p className="text-xs text-slate-400">Últimos 15 min</p>
          </CardContent>
        </Card>
      </div>

      {/* Componentes */}
      <Card data-testid="components-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Componentes do Sistema</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { name: 'API Backend (FastAPI)', icon: Server, status: status?.last_check?.status === 'operational' ? 'ok' : 'error' },
              { name: 'Banco de Dados (MongoDB)', icon: Database, status: status?.last_check?.details?.mongodb === 'ok' ? 'ok' : status?.last_check?.status === 'operational' ? 'ok' : 'error' },
              { name: 'Rede / Conectividade', icon: Wifi, status: 'ok' },
            ].map((comp) => (
              <div key={comp.name} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-3">
                  <comp.icon className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-slate-700">{comp.name}</span>
                </div>
                <Badge variant={comp.status === 'ok' ? 'default' : 'destructive'} className={comp.status === 'ok' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}>
                  {comp.status === 'ok' ? 'Operacional' : 'Indisponível'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Histórico de Disponibilidade (30 dias) */}
      {history.length > 0 && (
        <Card data-testid="availability-history">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Disponibilidade (Últimos 30 dias)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-0.5 h-16">
              {history.map((day) => {
                const avail = day.availability || 100;
                const color = avail >= 99.5 ? 'bg-emerald-400' : avail >= 97 ? 'bg-amber-400' : 'bg-red-400';
                return (
                  <div
                    key={day.date}
                    className={`flex-1 ${color} rounded-t-sm cursor-pointer hover:opacity-80 transition-opacity`}
                    style={{ height: `${Math.max((avail / 100) * 100, 5)}%` }}
                    title={`${day.date}: ${avail.toFixed(2)}% (${day.total_checks} checks)`}
                  />
                );
              })}
            </div>
            <div className="flex justify-between mt-1 text-xs text-slate-400">
              <span>{history[0]?.date}</span>
              <span>{history[history.length - 1]?.date}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Incidentes Abertos */}
      <Card data-testid="open-incidents">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Incidentes Abertos</CardTitle>
        </CardHeader>
        <CardContent>
          {incidents.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">Nenhum incidente aberto</p>
          ) : (
            <div className="space-y-3">
              {incidents.map((inc, i) => (
                <div key={inc.id || i} className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
                  <Badge variant="destructive" className="mt-0.5 text-xs shrink-0">{inc.severity}</Badge>
                  <div>
                    <p className="text-sm font-medium text-slate-700">{inc.title}</p>
                    <p className="text-xs text-slate-400">{inc.description}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {inc.created_at ? new Date(inc.created_at).toLocaleString('pt-BR') : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
